import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { get } from '@/api/client';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { EmptyState } from '@/components/atoms/EmptyState';

interface FollowUser {
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
  bio: string | null;
}

export function UserFollowersPage() {
  const { userId } = useParams<{ userId: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['users', userId, 'followers'],
    queryFn: () => get<{ list: FollowUser[] }>(`/users/${userId}/followers`),
    enabled: !!userId,
  });

  const count = data?.list?.length ?? 0;

  return (
    <div className="container-base py-10">
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 sm:p-8">
        <header className="mb-6">
          <h1 className="text-3xl font-extrabold">팔로워</h1>
          <p className="mt-2 text-sm text-[var(--color-text-sub)]">
            {count > 0
              ? `${count.toLocaleString('ko-KR')}명이 이 유저를 팔로우 중이에요.`
              : '이 유저를 팔로우하는 사람이 있으면 여기에 표시돼요.'}
          </p>
        </header>

        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        )}

        {data?.list && data.list.length > 0 ? (
          <ul className="space-y-2">
            {data.list.map((u) => (
              <li key={u.user_id}>
                <Link
                  to={`/users/${u.user_id}`}
                  className="flex items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition-colors hover:border-[var(--color-primary)]/40"
                >
                  <Avatar src={u.profile_image_url ?? undefined} alt={u.nickname} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold">{u.nickname}</span>
                      <LevelBadge level={u.level} size="sm" />
                    </div>
                    {u.bio && (
                      <p className="mt-0.5 line-clamp-1 text-sm text-[var(--color-text-sub)]">
                        {u.bio}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          !isLoading && (
            <EmptyState
              icon={Users}
              title="팔로워가 없어요"
              description="좋은 글이나 매매일지를 꾸준히 올리면 팔로워가 생겨요."
            />
          )
        )}
      </div>
    </div>
  );
}
